import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import {
  BellIcon,
  CheckCircleIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  UsersIcon,
  ClockIcon as ClockOutlineIcon,
  ArchiveBoxIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid, BellAlertIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../../context/AuthContext';
import AnnouncementService from './AnnouncementService';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds} วินาที`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins} นาที ${secs} วินาที` : `${mins} นาที`;
};

const ImageLightbox = ({ src, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.88)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#fff'
        }}
      >
        <XMarkIcon style={{ width: 22, height: 22 }} />
      </button>
      <img
        src={src}
        alt="ขยาย"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90vw',
          height: '88vh',
          borderRadius: '10px',
          objectFit: 'contain',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)'
        }}
      />
    </div>
  );
};

const BlockRenderer = ({ blocks }) => {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  if (!blocks || blocks.length === 0) return null;
  return (
    <>
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
      <div>
        {blocks.map((block, idx) => {
          if (block.type === 'image') {
            return (
              <div key={idx} style={{ textAlign: 'center', margin: '12px 0' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={block.content}
                    alt={`รูปภาพที่ ${idx + 1}`}
                    onClick={() => setLightboxSrc(block.content)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '420px',
                      borderRadius: '10px',
                      objectFit: 'contain',
                      cursor: 'zoom-in',
                      display: 'block',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                      transition: 'transform 0.15s, box-shadow 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.01)';
                      e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 2px 12px rgba(0,0,0,0.12)';
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.45)',
                    borderRadius: '6px',
                    padding: '3px 6px',
                    color: '#fff',
                    pointerEvents: 'none'
                  }}>
                    <MagnifyingGlassPlusIcon style={{ width: 14, height: 14 }} />
                  </div>
                </div>
              </div>
            );
          }
          if (block.type === 'html') {
            // Rendered in a fully-sandboxed iframe (no allow-scripts / no allow-same-origin):
            // scripts can't run and it can't touch the app, so raw HTML (incl. <style>) is
            // safe to render as-is — no sanitizer needed.
            return (
              <iframe
                key={idx}
                title={`ประกาศ HTML ${idx + 1}`}
                sandbox=""
                srcDoc={block.content}
                style={{
                  width: '100%',
                  height: '85vh',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  margin: '12px 0',
                  background: '#fff'
                }}
              />
            );
          }
          return (
            <div key={idx} style={{ margin: '8px 0' }}>
              <p style={{ margin: 0, lineHeight: '1.7', whiteSpace: 'pre-wrap', color: '#374151' }}>
                {block.content}
              </p>
            </div>
          );
        })}
      </div>
    </>
  );
};

/* Timer runs silently in background — elapsed seconds tracked but not shown */
const ReadTimer = ({ onAccept }) => {
  const elapsedRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => { elapsedRef.current += 1; }, 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleAccept = (pin = false) => {
    clearInterval(intervalRef.current);
    onAccept(elapsedRef.current, pin);
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px',
      paddingTop: '16px',
      borderTop: '1px solid #e5e7eb'
    }}>
      <button
        onClick={() => handleAccept(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#f59e0b',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(245,158,11,0.35)'
        }}
        title="รับทราบและปักหมุดไว้ด้านบนสำหรับตัวเอง"
      >
        📌 รับทราบ + ปักหมุด
      </button>
      <button
        onClick={() => handleAccept(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#10b981',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(16,185,129,0.35)'
        }}
      >
        <CheckCircleIcon style={{ width: 18, height: 18 }} />
        รับทราบ
      </button>
    </div>
  );
};

const STATUS_CONFIG = {
  draft:     { label: 'ร่าง',     color: '#92400e', bg: '#fef9c3', border: '#fde047', dotColor: '#eab308' },
  published: { label: 'เผยแพร่', color: '#065f46', bg: '#f0fdf4', border: '#6ee7b7', dotColor: '#10b981' },
  hidden:    { label: 'ซ่อน',    color: '#374151', bg: '#f3f4f6', border: '#d1d5db', dotColor: '#9ca3af' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.published;
  const Icon = status === 'draft' ? ArchiveBoxIcon : status === 'hidden' ? EyeSlashIcon : null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      fontSize: '11px', background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, padding: '2px 8px',
      borderRadius: '20px', fontWeight: '700'
    }}>
      {Icon && <Icon style={{ width: 11, height: 11 }} />}
      {cfg.label}
    </span>
  );
};

const StatusHistoryTimeline = ({ history }) => {
  if (!Array.isArray(history) || history.length === 0) return null;
  return (
    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        ประวัติสถานะ
      </div>
      <div style={{ position: 'relative', paddingLeft: '20px' }}>
        {/* vertical line */}
        <div style={{ position: 'absolute', left: 6, top: 6, bottom: 6, width: 2, background: '#e5e7eb', borderRadius: '2px' }} />
        {history.map((event, idx) => {
          const cfg = STATUS_CONFIG[event.status] || STATUS_CONFIG.published;
          const isLast = idx === history.length - 1;
          return (
            <div key={idx} style={{ position: 'relative', marginBottom: isLast ? 0 : '10px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              {/* dot */}
              <div style={{
                position: 'absolute', left: -17,
                width: 10, height: 10, borderRadius: '50%',
                background: cfg.dotColor,
                border: '2px solid #fff',
                boxShadow: `0 0 0 2px ${cfg.dotColor}`,
                marginTop: 3, flexShrink: 0
              }} />
              <div>
                <StatusBadge status={event.status} />
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  {event.changedAt ? formatDate(event.changedAt) : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* Role colour config — superuser gets purple accent, admin gets teal accent */
const ROLE_STYLE = {
  superuser: { bg: '#f5f3ff', border: '#e5e7eb', accent: '#7c3aed' },
  admin:     { bg: '#f0fdfa', border: '#e5e7eb', accent: '#0d9488' },
  default:   { bg: '#f9fafb', border: '#e5e7eb', accent: '#6b7280' }
};

const getRoleStyle = (role) => ROLE_STYLE[role] || ROLE_STYLE.default;

const formatCommentDate = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'เมื่อกี้';
  if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs} ชั่วโมงที่แล้ว`;
  return d.toLocaleDateString('th-TH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CommentSection = ({ announcementId, currentUser, isSuperuser }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    AnnouncementService.getComments(announcementId)
      .then(res => { if (mounted) setComments(res.data); })
      .catch(() => { if (mounted) toast.error('โหลดความคิดเห็นล้มเหลว'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [announcementId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const res = await AnnouncementService.createComment(announcementId, text.trim(), imageFiles);
      setComments(prev => [...prev, res.data]);
      setText('');
      setImageFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch {
      toast.error('ส่งความคิดเห็นล้มเหลว');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const total = imageFiles.length + selected.length;
    if (total > 5) {
      toast.warning('แนบรูปได้สูงสุด 5 รูป');
      const allowed = selected.slice(0, 5 - imageFiles.length);
      setImageFiles(prev => [...prev, ...allowed]);
    } else {
      setImageFiles(prev => [...prev, ...selected]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    try {
      await AnnouncementService.deleteComment(announcementId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      toast.error('ลบล้มเหลว');
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit(e);
    }
  };

  return (
    <div style={{
      marginTop: '20px',
      paddingTop: '20px',
      borderTop: '1px solid #e5e7eb'
    }}>
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
      {/* Comment section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
        <ChatBubbleLeftIcon style={{ width: 16, height: 16, color: '#6b7280' }} />
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
          ความคิดเห็น
        </span>
        {!loading && (
          <span style={{
            fontSize: '11px',
            background: '#e5e7eb',
            color: '#6b7280',
            borderRadius: '20px',
            padding: '1px 7px',
            fontWeight: '600'
          }}>
            {comments.length}
          </span>
        )}
      </div>

      {/* Comment list */}
      {loading ? (
        <div style={{ color: '#9ca3af', fontSize: '13px', padding: '8px 0' }}>กำลังโหลด...</div>
      ) : comments.length === 0 ? (
        <div style={{
          color: '#9ca3af',
          fontSize: '13px',
          textAlign: 'center',
          padding: '16px 0',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px dashed #e5e7eb',
          marginBottom: '12px'
        }}>
          ยังไม่มีความคิดเห็น — เป็นคนแรกที่ถามหรือแสดงความเห็น
        </div>
      ) : (
        <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {comments.map(comment => {
            const role = comment.author?.role || 'default';
            const rs = getRoleStyle(role);
            const isOwn = comment.userId === currentUser?.id;
            const canDelete = isSuperuser || isOwn;

            return (
              <div
                key={comment.id}
                style={{
                  background: rs.bg,
                  border: `1px solid ${rs.border}`,
                  borderLeft: `3px solid ${rs.accent}`,
                  borderRadius: '10px',
                  padding: '10px 14px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                    {comment.author?.nickname || comment.author?.email || 'Unknown'}
                  </span>
                  <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>
                    {formatCommentDate(comment.createdAt)}
                  </span>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      disabled={deletingId === comment.id}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: deletingId === comment.id ? 'not-allowed' : 'pointer',
                        color: '#9ca3af',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        opacity: deletingId === comment.id ? 0.4 : 1
                      }}
                      title="ลบ"
                    >
                      <XMarkIcon style={{ width: 14, height: 14 }} />
                    </button>
                  )}
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#374151',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {comment.content}
                </p>
                {Array.isArray(comment.imageUrls) && comment.imageUrls.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {comment.imageUrls.map((url, imgIdx) => (
                      <img
                        key={imgIdx}
                        src={url}
                        alt=""
                        onClick={() => setLightboxSrc(url)}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: '6px',
                          cursor: 'zoom-in',
                          border: '1px solid #e5e7eb'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New comment form */}
      <form onSubmit={handleSubmit} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          onKeyDown={handleKeyDown}
          placeholder="ถามหรือแสดงความเห็น... (Ctrl+Enter เพื่อส่ง)"
          rows={2}
          style={{
            width: '100%',
            border: 'none',
            borderBottom: '1px solid #f3f4f6',
            padding: '10px 14px',
            fontSize: '13px',
            resize: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            overflow: 'hidden',
            minHeight: '60px',
            outline: 'none'
          }}
        />

        {/* Image previews */}
        {imageFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '8px 14px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
            {imageFiles.map((file, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <img
                  src={URL.createObjectURL(file)}
                  alt=""
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    background: '#ef4444', border: 'none', borderRadius: '50%',
                    width: 18, height: 18, cursor: 'pointer', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                  }}
                >
                  <XMarkIcon style={{ width: 10, height: 10 }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar + send */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', background: '#fafafa', gap: '8px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={imageFiles.length >= 5}
            title={imageFiles.length >= 5 ? 'แนบรูปได้สูงสุด 5 รูป' : 'แนบรูปภาพ'}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: '6px',
              padding: '4px 10px', cursor: imageFiles.length >= 5 ? 'not-allowed' : 'pointer',
              color: '#6b7280', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px',
              opacity: imageFiles.length >= 5 ? 0.4 : 1
            }}
          >
            <PhotoIcon style={{ width: 14, height: 14 }} />
            รูป {imageFiles.length > 0 ? `(${imageFiles.length}/5)` : ''}
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="submit"
            disabled={submitting || !text.trim()}
            style={{
              background: submitting || !text.trim() ? '#d1d5db' : '#3b82f6',
              color: '#fff', border: 'none', borderRadius: '6px',
              padding: '6px 16px', cursor: submitting || !text.trim() ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '13px', fontWeight: '600'
            }}
          >
            <PaperAirplaneIcon style={{ width: 14, height: 14 }} />
            {submitting ? 'กำลังส่ง...' : 'ส่ง'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReadStatsPanel = ({ announcementId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || stats) return;
    setLoading(true);
    AnnouncementService.getReadStats(announcementId)
      .then(res => setStats(res.data))
      .catch(() => toast.error('โหลดสถิติล้มเหลว'))
      .finally(() => setLoading(false));
  }, [open, announcementId, stats]);

  const readList = stats?.stats.filter(s => s.hasRead) || [];
  const unreadList = stats?.stats.filter(s => !s.hasRead) || [];

  return (
    <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '6px 14px', cursor: 'pointer', color: '#374151', fontSize: '13px', fontWeight: '600'
        }}
      >
        <UsersIcon style={{ width: 15, height: 15 }} />
        สถิติการอ่าน
        {stats && (
          <span style={{ color: '#6b7280', fontWeight: '400' }}>
            ({readList.length}/{stats.totalActive} อ่านแล้ว)
          </span>
        )}
        {open ? <ChevronUpIcon style={{ width: 14, height: 14 }} /> : <ChevronDownIcon style={{ width: 14, height: 14 }} />}
      </button>

      {open && (
        <div style={{ marginTop: '12px' }}>
          {loading ? (
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>กำลังโหลด...</div>
          ) : !stats ? null : (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {/* Unread */}
              {unreadList.length > 0 && (
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#dc2626', marginBottom: '6px' }}>
                    ยังไม่ได้อ่าน ({unreadList.length})
                  </div>
                  {unreadList.map(s => (
                    <div key={s.adminId} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '5px 8px', borderRadius: '6px', background: '#fef2f2',
                      marginBottom: '4px', fontSize: '13px', color: '#374151'
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                      {s.nickname}
                    </div>
                  ))}
                </div>
              )}
              {/* Read */}
              {readList.length > 0 && (
                <div style={{ flex: 2, minWidth: '260px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#059669', marginBottom: '6px' }}>
                    อ่านแล้ว ({readList.length})
                  </div>
                  {readList.map(s => (
                    <div key={s.adminId} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '5px 8px', borderRadius: '6px', background: '#f0fdf4',
                      marginBottom: '4px', fontSize: '13px'
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                      <span style={{ color: '#374151', fontWeight: '600', minWidth: '80px' }}>{s.nickname}</span>
                      {s.readDurationSeconds != null && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#6b7280', fontSize: '12px' }}>
                          <ClockOutlineIcon style={{ width: 12, height: 12 }} />
                          {formatDuration(s.readDurationSeconds)}
                        </span>
                      )}
                      {s.readAt && (
                        <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: 'auto' }}>
                          {formatDate(s.readAt)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AnnouncementCard = ({
  announcement,
  isSuperuser,
  currentUser,
  onRead,
  onPin,
  onDelete,
  onEdit,
  defaultExpanded
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [blocks, setBlocks] = useState(announcement.blocks || null);
  const [accepting, setAccepting] = useState(false);

  const handleToggle = async () => {
    if (!expanded && announcement.isRead && !blocks) {
      setLoadingContent(true);
      try {
        const res = await AnnouncementService.getAnnouncement(announcement.id);
        setBlocks(res.data.blocks);
      } catch {
        toast.error('โหลดเนื้อหาล้มเหลว');
      } finally {
        setLoadingContent(false);
      }
    }
    setExpanded(prev => !prev);
  };

  const handleAccept = async (durationSeconds, pin = false) => {
    setAccepting(true);
    try {
      await onRead(announcement.id, durationSeconds, pin);
    } finally {
      setAccepting(false);
    }
  };

  const isUnread = !announcement.isRead;
  const hasPinIcon = announcement.isPinned;
  const isUserPinned = !!announcement.userPinned;
  const status = announcement.status || 'published';
  const isDraft = status === 'draft';
  const isHidden = status === 'hidden';
  const isNonPublished = isDraft || isHidden;

  const cardBorder = isDraft
    ? '2px dashed #fde047'
    : isHidden
      ? '2px dashed #d1d5db'
      : isUnread
        ? '2px solid #3b82f6'
        : isUserPinned
          ? '2px solid #f59e0b'
          : '1px solid #e5e7eb';

  const cardBackground = isDraft ? '#fffbeb' : isHidden ? '#f9fafb' : '#fff';

  const cardShadow = isDraft
    ? '0 1px 4px rgba(234,179,8,0.15)'
    : isHidden
      ? 'none'
      : isUnread
        ? '0 4px 20px rgba(59,130,246,0.15)'
        : '0 1px 4px rgba(0,0,0,0.06)';

  return (
    <div style={{
      background: cardBackground,
      border: cardBorder,
      borderRadius: '12px',
      marginBottom: '16px',
      overflow: 'hidden',
      boxShadow: cardShadow,
      transition: 'box-shadow 0.2s'
    }}>
      {/* Card Header */}
      <div
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 20px',
          cursor: 'pointer',
          background: isDraft
            ? '#fef9c3'
            : isHidden
              ? '#f3f4f6'
              : isUnread
                ? 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)'
                : '#fafafa',
          borderBottom: expanded ? '1px solid #e5e7eb' : 'none',
          userSelect: 'none'
        }}
      >
        {/* Left: icon + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: isDraft ? '#eab308' : isHidden ? '#9ca3af' : isUnread ? '#3b82f6' : '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {isDraft
              ? <ArchiveBoxIcon style={{ width: 20, height: 20, color: '#fff' }} />
              : isHidden
                ? <EyeSlashIcon style={{ width: 20, height: 20, color: '#fff' }} />
                : isUnread
                  ? <BellAlertIcon style={{ width: 20, height: 20, color: '#fff' }} />
                  : <CheckCircleSolid style={{ width: 20, height: 20, color: '#fff' }} />
            }
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {isNonPublished && <StatusBadge status={status} />}
              {hasPinIcon && (
                <span style={{
                  fontSize: '11px',
                  background: '#f59e0b',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  fontWeight: '600'
                }}>📌 ปักหมุด</span>
              )}
              {isUserPinned && (
                <span style={{
                  fontSize: '11px',
                  background: '#fef3c7',
                  color: '#92400e',
                  border: '1px solid #f59e0b',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  fontWeight: '600'
                }}>📌 ของฉัน</span>
              )}
              {isUnread && !isNonPublished && (
                <span style={{
                  fontSize: '11px',
                  background: '#3b82f6',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  fontWeight: '600'
                }}>ใหม่</span>
              )}
              <span style={{
                fontWeight: isUnread ? '700' : '600',
                fontSize: '15px',
                color: '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {announcement.title}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {formatDate(announcement.createdAt)}
              {announcement.creator && ` · โดย ${announcement.creator.nickname || announcement.creator.email}`}
              {announcement.isRead && (
                <span style={{ marginLeft: '8px', color: '#059669' }}>
                  ✓ อ่านแล้ว
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: actions + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
          {announcement.isRead && !isNonPublished && (
            <button
              onClick={(e) => { e.stopPropagation(); onPin(announcement.id, !isUserPinned); }}
              style={{
                background: isUserPinned ? '#fef3c7' : 'none',
                border: isUserPinned ? '1px solid #f59e0b' : '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '4px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '13px'
              }}
              title={isUserPinned ? 'เลิกปักหมุด' : 'ปักหมุดไว้ด้านบนสำหรับตัวเอง'}
            >
              📌
            </button>
          )}
          {isSuperuser && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(announcement); }}
                style={{
                  background: 'none',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="แก้ไข"
              >
                <PencilIcon style={{ width: 14, height: 14 }} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(announcement.id); }}
                style={{
                  background: 'none',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="ลบ"
              >
                <TrashIcon style={{ width: 14, height: 14 }} />
              </button>
            </>
          )}
          <div style={{ color: '#9ca3af' }}>
            {expanded
              ? <ChevronUpIcon style={{ width: 18, height: 18 }} />
              : <ChevronDownIcon style={{ width: 18, height: 18 }} />
            }
          </div>
        </div>
      </div>

      {/* Card Body */}
      {expanded && (
        <div style={{ padding: '20px 24px' }}>
          {loadingContent ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>
              กำลังโหลด...
            </div>
          ) : (
            <>
              <BlockRenderer blocks={blocks} />
              {isUnread && !accepting && !isNonPublished && (
                <ReadTimer onAccept={handleAccept} />
              )}
              {accepting && (
                <div style={{ color: '#9ca3af', fontSize: '13px', marginTop: '12px' }}>
                  กำลังบันทึก...
                </div>
              )}
              {isSuperuser && (
                <StatusHistoryTimeline history={announcement.statusHistory} />
              )}
              <CommentSection
                announcementId={announcement.id}
                currentUser={currentUser}
                isSuperuser={isSuperuser}
              />
              {isSuperuser && !isNonPublished && (
                <ReadStatsPanel announcementId={announcement.id} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AnnouncementList = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const isSuperuser = authState?.user?.role === 'superuser';
  const currentUser = authState?.user;

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AnnouncementService.getAnnouncements();
      setAnnouncements(res.data);
    } catch {
      toast.error('โหลดประกาศล้มเหลว');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handleRead = async (id, readDurationSeconds, pin = false) => {
    try {
      await AnnouncementService.markAnnouncementRead(id, readDurationSeconds, pin);
      setAnnouncements(prev =>
        prev.map(a =>
          a.id === id
            ? { ...a, isRead: true, readRecord: { readDurationSeconds }, userPinned: pin }
            : a
        )
      );
      toast.success(pin ? 'บันทึกการอ่านและปักหมุดแล้ว' : 'บันทึกการอ่านแล้ว');
    } catch {
      toast.error('บันทึกล้มเหลว');
    }
  };

  const handlePin = async (id, pinned) => {
    try {
      await AnnouncementService.pinAnnouncement(id, pinned);
      setAnnouncements(prev =>
        prev.map(a => (a.id === id ? { ...a, userPinned: pinned } : a))
      );
      toast.success(pinned ? 'ปักหมุดแล้ว' : 'เลิกปักหมุดแล้ว');
    } catch {
      toast.error('บันทึกล้มเหลว');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await AnnouncementService.deleteAnnouncement(deleteId);
      setAnnouncements(prev => prev.filter(a => a.id !== deleteId));
      toast.success('ลบประกาศแล้ว');
    } catch {
      toast.error('ลบล้มเหลว');
    } finally {
      setIsDeleteOpen(false);
      setDeleteId(null);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleEdit = (announcement) => {
    navigate('/admin/announcement/create', { state: { editing: announcement } });
  };

  const draftAnnouncements = isSuperuser ? announcements.filter(a => a.status === 'draft') : [];
  const hiddenAnnouncements = isSuperuser ? announcements.filter(a => a.status === 'hidden') : [];
  const publishedAnnouncements = announcements.filter(a => a.status === 'published' || !a.status);
  const unreadAnnouncements = publishedAnnouncements.filter(a => !a.isRead);
  // Same ranking as the backend: creator pin > my pin > rest (stable sort keeps newest-first
  // within each group). Re-applied locally so pin/unpin reorders without a refetch.
  const pinRank = (a) => (a.isPinned ? 0 : a.userPinned ? 1 : 2);
  const readAnnouncements = publishedAnnouncements
    .filter(a => a.isRead)
    .sort((a, b) => pinRank(a) - pinRank(b));

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '28px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BellIcon style={{ width: 26, height: 26, color: '#fff' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#111827' }}>
              ประกาศ / แจ้งข่าว
            </h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
              {unreadAnnouncements.length > 0
                ? `มี ${unreadAnnouncements.length} ประกาศที่ยังไม่ได้อ่าน`
                : 'อ่านครบทุกประกาศแล้ว'}
              {draftAnnouncements.length > 0 && (
                <span style={{ marginLeft: '8px', color: '#ca8a04' }}>· {draftAnnouncements.length} ร่าง</span>
              )}
              {hiddenAnnouncements.length > 0 && (
                <span style={{ marginLeft: '8px', color: '#6b7280' }}>· {hiddenAnnouncements.length} ซ่อน</span>
              )}
            </p>
          </div>
        </div>
        {isSuperuser && (
          <button
            onClick={() => navigate('/admin/announcement/create')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 18px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <PlusIcon style={{ width: 18, height: 18 }} />
            สร้างประกาศใหม่
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
          กำลังโหลด...
        </div>
      ) : announcements.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 0',
          color: '#9ca3af',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '1px dashed #d1d5db'
        }}>
          <BellIcon style={{ width: 48, height: 48, margin: '0 auto 12px', opacity: 0.4 }} />
          <p style={{ margin: 0 }}>ยังไม่มีประกาศ</p>
        </div>
      ) : (
        <>
          {/* Drafts Section — superuser only */}
          {draftAnnouncements.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <ArchiveBoxIcon style={{ width: 18, height: 18, color: '#ca8a04' }} />
                <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#92400e' }}>
                  ร่างประกาศ ({draftAnnouncements.length})
                </h2>
                <span style={{ fontSize: '11px', background: '#fef9c3', color: '#713f12', border: '1px solid #fde047', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' }}>
                  มองเห็นได้เฉพาะ Superuser
                </span>
              </div>
              {draftAnnouncements.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} isSuperuser={isSuperuser} currentUser={currentUser} onRead={handleRead} onPin={handlePin} onDelete={handleDelete} onEdit={handleEdit} defaultExpanded={false} />
              ))}
            </div>
          )}

          {/* Unread Section */}
          {unreadAnnouncements.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <BellAlertIcon style={{ width: 18, height: 18, color: '#3b82f6' }} />
                <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1d4ed8' }}>
                  ยังไม่ได้อ่าน ({unreadAnnouncements.length})
                </h2>
              </div>
              {unreadAnnouncements.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} isSuperuser={isSuperuser} currentUser={currentUser} onRead={handleRead} onPin={handlePin} onDelete={handleDelete} onEdit={handleEdit} defaultExpanded={false} />
              ))}
            </div>
          )}

          {/* Read Section */}
          {readAnnouncements.length > 0 && (
            <div style={{ marginBottom: hiddenAnnouncements.length > 0 ? '32px' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <CheckCircleSolid style={{ width: 18, height: 18, color: '#10b981' }} />
                <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                  อ่านแล้ว ({readAnnouncements.length})
                </h2>
              </div>
              {readAnnouncements.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} isSuperuser={isSuperuser} currentUser={currentUser} onRead={handleRead} onPin={handlePin} onDelete={handleDelete} onEdit={handleEdit} defaultExpanded={false} />
              ))}
            </div>
          )}

          {/* Hidden Section — superuser only */}
          {hiddenAnnouncements.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <EyeSlashIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
                <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                  ซ่อน ({hiddenAnnouncements.length})
                </h2>
                <span style={{ fontSize: '11px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' }}>
                  มองเห็นได้เฉพาะ Superuser
                </span>
              </div>
              {hiddenAnnouncements.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} isSuperuser={isSuperuser} currentUser={currentUser} onRead={handleRead} onPin={handlePin} onDelete={handleDelete} onEdit={handleEdit} defaultExpanded={false} />
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={isDeleteOpen}
        onRequestClose={() => { setIsDeleteOpen(false); setDeleteId(null); }}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 },
          content: {
            top: '50%', left: '50%', right: 'auto', bottom: 'auto',
            transform: 'translate(-50%,-50%)',
            width: '400px', height: 'auto'
          }
        }}
      >
        <DeleteConfirm
          titleText="ต้องการลบประกาศนี้ใช่ไหม?"
          confirmFunction={handleDeleteConfirm}
          closeConfirm={() => { setIsDeleteOpen(false); setDeleteId(null); }}
        />
      </Modal>
    </div>
  );
};

export default AnnouncementList;
